/**
 * Interface for localStorage items with expiration
 */
export interface IStorageItemWithExpiration {
	value: string;
	expiration_date: string;
}

/**
 * Interface for user preferences object
 */
export interface IUserPreferences {
	[key: string]: IStorageItemWithExpiration;
}

/**
 * The key used in localStorage to store user preferences
 */
export const USER_PREFERENCES_KEY = 'user_preferences';

/**
 * Checks if a value is a storage item with expiration
 */
export const isStorageItemWithExpiration = (
	item: any
): item is IStorageItemWithExpiration => {
	return (
		item &&
		typeof item === 'object' &&
		'value' in item &&
		'expiration_date' in item &&
		typeof item.value === 'string' &&
		typeof item.expiration_date === 'string'
	);
};

/**
 * Checks if a storage item has expired
 */
export const isStorageItemExpired = (
	item: IStorageItemWithExpiration
): boolean => {
	const expirationDate = new Date(item.expiration_date);
	const now = new Date();
	return expirationDate < now;
};

/**
 * Gets all valid user preferences from localStorage
 * Returns an object with property names as keys and their values
 * Also removes expired items and updates localStorage
 */
export const getValidStorageItemsWithExpiration = (): Record<
	string,
	string
> => {
	const validItems: Record<string, string> = {};

	try {
		const rawPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
		if (!rawPreferences) return validItems;

		const preferences: IUserPreferences = JSON.parse(rawPreferences);
		const updatedPreferences: IUserPreferences = {};
		let hasExpiredItems = false;

		// Check each property in user_preferences
		for (const [key, item] of Object.entries(preferences)) {
			if (isStorageItemWithExpiration(item)) {
				if (isStorageItemExpired(item)) {
					// Mark that we have expired items
					hasExpiredItems = true;
				} else {
					// Keep valid items
					validItems[key] = item.value;
					updatedPreferences[key] = item;
				}
			}
		}

		// Update localStorage if there were expired items
		if (hasExpiredItems) {
			if (Object.keys(updatedPreferences).length > 0) {
				localStorage.setItem(
					USER_PREFERENCES_KEY,
					JSON.stringify(updatedPreferences)
				);
			} else {
				localStorage.removeItem(USER_PREFERENCES_KEY);
			}
		}
	} catch (error) {
		// Invalid JSON or other error, return empty object
		console.error('Error parsing user preferences:', error);
	}

	return validItems;
};

/**
 * Removes specified keys from user_preferences in localStorage
 */
export const removeStorageItems = (keys: string[]): void => {
	try {
		const rawPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
		if (!rawPreferences) return;

		const preferences: IUserPreferences = JSON.parse(rawPreferences);

		// Remove specified keys
		keys.forEach((key) => {
			delete preferences[key];
		});

		// Update or remove the user_preferences object
		if (Object.keys(preferences).length > 0) {
			localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferences));
		} else {
			localStorage.removeItem(USER_PREFERENCES_KEY);
		}
	} catch (error) {
		console.error('Error removing storage items:', error);
	}
};

/**
 * Gets a specific item from user_preferences
 * Returns null if expired or doesn't exist
 * Removes the item if expired
 */
export const getStorageItemWithExpiration = (key: string): string | null => {
	try {
		const rawPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
		if (!rawPreferences) return null;

		const preferences: IUserPreferences = JSON.parse(rawPreferences);
		const item = preferences[key];

		if (!item || !isStorageItemWithExpiration(item)) return null;

		if (isStorageItemExpired(item)) {
			// Remove expired item
			delete preferences[key];
			if (Object.keys(preferences).length > 0) {
				localStorage.setItem(
					USER_PREFERENCES_KEY,
					JSON.stringify(preferences)
				);
			} else {
				localStorage.removeItem(USER_PREFERENCES_KEY);
			}
			return null;
		}

		return item.value;
	} catch (error) {
		return null;
	}
};

/**
 * Sets an item in user_preferences with expiration
 */
export const setStorageItemWithExpiration = (
	key: string,
	value: string,
	expirationDate: Date | string
): void => {
	try {
		// Get existing preferences or create new object
		let preferences: IUserPreferences = {};
		const rawPreferences = localStorage.getItem(USER_PREFERENCES_KEY);
		if (rawPreferences) {
			preferences = JSON.parse(rawPreferences);
		}

		// Add or update the item
		preferences[key] = {
			value,
			expiration_date:
				typeof expirationDate === 'string'
					? expirationDate
					: expirationDate.toISOString(),
		};

		// Save back to localStorage
		localStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(preferences));
	} catch (error) {
		console.error('Error setting storage item:', error);
	}
};
